/*
 * cart-attributes-sync.js
 *
 * /cart.js で現在のカートを取得し、application_data JSON を再構築して
 * /cart/update.js の attributes として上書きする best-effort 同期関数。
 *
 * 失敗しても呼び出し元の処理（/cart/add.js 成功後のフロー）を止めないよう
 * 例外を伝播させず result.ok=false を返す。
 */

(function () {
  const isNodeEnv = typeof module !== 'undefined' && module.exports;

  const cartAttributes = isNodeEnv
    ? require('./cart-attributes.js')
    : (typeof window !== 'undefined' ? window.MFCartAttributes : null);

  if (!cartAttributes) {
    if (typeof console !== 'undefined' && typeof console.error === 'function') {
      console.error('cart-attributes.js must be loaded before cart-attributes-sync.js');
    }
    return;
  }

  const { APPLICATION_DATA_ATTRIBUTE_KEY, serializeApplicationData } = cartAttributes;

  let inFlightSyncPromise = null;
  let pendingSyncRequest = null;

  async function runSyncApplicationDataAttribute(options = {}) {
    const {
      fetchImpl,
      cart: providedCart = null,
      cartUrl = '/cart.js',
      cartUpdateUrl = '/cart/update.js',
      validateCart,
      now,
      consoleObj = (typeof console !== 'undefined' ? console : null),
    } = options;

    if (typeof fetchImpl !== 'function') {
      return { ok: false, skipped: true };
    }

    const warn = (...args) => {
      if (consoleObj && typeof consoleObj.warn === 'function') consoleObj.warn(...args);
    };

    try {
      let cart = providedCart;
      if (!cart) {
        const res = await fetchImpl(cartUrl, {
          method: 'GET',
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
        });
        if (!res || !res.ok) {
          warn('cart-attributes-sync: failed to fetch cart', res && res.status);
          return { ok: false, error: new Error(`cart fetch failed: ${res && res.status}`) };
        }
        cart = await res.json();
      }

      const validationMessage = typeof validateCart === 'function' ? validateCart(cart) : '';
      if (validationMessage) return { ok: false, validationMessage };

      const serialized = serializeApplicationData(cart, { now });
      const body = JSON.stringify({
        attributes: { [APPLICATION_DATA_ATTRIBUTE_KEY]: serialized },
      });

      const updateRes = await fetchImpl(cartUpdateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/json',
        },
        body,
      });

      if (!updateRes || !updateRes.ok) {
        warn('cart-attributes-sync: failed to update cart attributes', updateRes && updateRes.status);
        return { ok: false, error: new Error(`cart update failed: ${updateRes && updateRes.status}`) };
      }

      return { ok: true };
    } catch (err) {
      warn('cart-attributes-sync: exception', err);
      return { ok: false, error: err };
    }
  }

  function createSyncRequest(options) {
    let resolvePromise;
    const promise = new Promise((resolve) => {
      resolvePromise = resolve;
    });

    return {
      options,
      resolvers: [resolvePromise],
      promise,
    };
  }

  async function drainSyncQueue(initialRequest) {
    let currentRequest = initialRequest;

    try {
      while (currentRequest) {
        const result = await runSyncApplicationDataAttribute(currentRequest.options);
        currentRequest.resolvers.forEach((resolve) => resolve(result));
        currentRequest = pendingSyncRequest;
        pendingSyncRequest = null;
      }
    } finally {
      inFlightSyncPromise = null;
    }
  }

  function syncApplicationDataAttribute(options = {}) {
    const request = createSyncRequest(options);

    if (inFlightSyncPromise) {
      if (pendingSyncRequest) {
        pendingSyncRequest.options = options;
        pendingSyncRequest.resolvers.push(...request.resolvers);
      } else {
        pendingSyncRequest = {
          options,
          resolvers: [...request.resolvers],
        };
      }
      return request.promise;
    }

    inFlightSyncPromise = drainSyncQueue({
      options,
      resolvers: [...request.resolvers],
    });
    return request.promise;
  }

  const cartAttributesSyncExports = { syncApplicationDataAttribute };

  if (isNodeEnv) {
    module.exports = cartAttributesSyncExports;
  }

  if (typeof window !== 'undefined') {
    window.MFCartAttributesSync = Object.assign({}, window.MFCartAttributesSync, cartAttributesSyncExports);
  }
})();
